/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  listNotifications,
  listNotificationsByUrl,
  getNotificationsUnreadCount,
  markNotificationsRead,
  markAllNotificationsRead,
  getNotificationPreferences,
  upsertNotificationPreference,
} from '../api/api';
import { NotificationsWSClient } from '../lib/wsNotifications';
import { emitTradeApprovalChange } from '../lib/tradeApprovalEvents';
import { toast } from 'sonner';

const NotificationsContext = createContext(null);

export const NotificationsProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ unread: undefined, category: undefined });
  const [preferences, setPreferences] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const wsRef = useRef(null);

  const mergeItems = useCallback((incoming) => {
    setItems((prev) => {
      const map = new Map(prev.map((n) => [n.id, n]));
      for (const n of incoming) map.set(n.id, { ...map.get(n.id), ...n });
      // Sort by created_at desc
      const arr = Array.from(map.values());
      arr.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      return arr;
    });
  }, []);

  const _extractPage = useCallback((data) => {
    let arr = [];
    let next = null;
    if (Array.isArray(data)) {
      arr = data;
    } else if (data && typeof data === 'object') {
      arr = data.results || data.items || data.notifications || [];
      next = data.next || null;
    }
    return { arr, next };
  }, []);

  const fetchList = useCallback(async (params = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setItems([]);
      setUnreadCount(0);
      setNextCursor(null);
      setHasMore(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, headers } = await listNotifications(params);
      const { arr, next } = _extractPage(data);
      // Reset items on fresh fetch (not appending)
      setItems(arr);
      setNextCursor(next);
      setHasMore(!!next);
      const headerCount = headers?.['x-notifications-unread'] || headers?.['X-Notifications-Unread'];
      if (headerCount != null) {
        const n = parseInt(Array.isArray(headerCount) ? headerCount[0] : headerCount, 10);
        if (!Number.isNaN(n)) setUnreadCount(n);
      } else {
        const count = await getNotificationsUnreadCount(
          params?.category ? { category: params.category } : undefined
        );
        setUnreadCount(count);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [_extractPage]);

  const fetchMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await listNotificationsByUrl(nextCursor);
      const { arr, next } = _extractPage(data);
      mergeItems(arr);
      setNextCursor(next);
      setHasMore(!!next);
    } catch (err) {
      void err;
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, _extractPage, mergeItems]);

  const refreshUnread = useCallback(async (category) => {
    const token = localStorage.getItem('token');
    if (!token) { setUnreadCount(0); return; }
    try {
      const count = await getNotificationsUnreadCount(category ? { category } : undefined);
      setUnreadCount(count);
    } catch (err) {
      void err;
    }
  }, []);

  const applyNewNotification = useCallback((n) => {
    mergeItems([n]);
    setUnreadCount((c) => c + 1);

    // Surface alert triggers as toasts with deep-link
    try {
      const isAlert = n?.category === 'ALERT' || n?.type === 'alerts.triggered';
      if (isAlert) {
        const alertId = n?.data?.alert_id || n?.alert_id || n?.metadata?.alert_id;
        const title = n?.title || 'Alert triggered';
        const desc = n?.body || n?.message || n?.data?.message || [n?.data?.symbol, n?.data?.timeframe].filter(Boolean).join(' ');
        toast.info(title, {
          description: desc,
          action: alertId ? {
            label: 'Open',
            onClick: () => { window.location.href = `/alerts/${encodeURIComponent(alertId)}`; },
          } : undefined,
        });
      }

      // Surface trade approval requests as toasts with deep-link
      const isApproval = n?.category === 'TRADE_APPROVAL';
      if (isApproval) {
        // Broadcast so Sidebar badge + PendingTradesPage re-fetch instantly
        emitTradeApprovalChange();

        const pendingId = n?.data?.pending_id;
        const title = n?.title || 'Trade Approval Required';
        const desc = n?.body || n?.message || n?.data?.message || '';
        toast.warning(title, {
          description: desc,
          duration: 15000,
          action: pendingId ? {
            label: 'Review',
            onClick: () => { window.location.href = `/approvals/${encodeURIComponent(pendingId)}`; },
          } : undefined,
        });
      }
    } catch (e) {
      // Never block notification pipeline on toast errors
      void e;
    }
  }, [mergeItems]);

  const markRead = useCallback(async (ids) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    await markNotificationsRead(ids);
    setItems((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, read_at: n.read_at || new Date().toISOString() } : n)));
    setUnreadCount((c) => Math.max(0, c - (Array.isArray(ids) ? ids.length : 1)));
  }, []);

  const markAll = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    await markAllNotificationsRead();
    setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
    refreshUnread();
  }, [refreshUnread]);

  const loadPreferences = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) { setPreferences([]); return; }
    try {
      const prefs = await getNotificationPreferences();
      setPreferences(prefs || []);
    } catch (err) {
      // ignore preference load errors silently
      String(err);
    }
  }, []);

  const upsertPreference = useCallback(async (pref) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const optimistic = [...preferences];
    setPreferences((prev) => {
      const idx = prev.findIndex(p => p.channel === pref.channel && p.category === pref.category);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...prev[idx], ...pref };
        return next;
      }
      return [...prev, pref];
    });
    try {
      await upsertNotificationPreference(pref);
  } catch (e) {
      // rollback
      setPreferences(optimistic);
      throw e;
    }
  }, [preferences]);

  // WebSocket lifecycle
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setReady(false);
      return undefined;
    }
    const client = new NotificationsWSClient({
      onReady: () => setReady(true),
      onNotification: applyNewNotification,
      onError: () => {},
    });
    wsRef.current = client;
    client.connect();
    return () => {
      client.close();
      wsRef.current = null;
    };
  }, [applyNewNotification]);

  // Initial load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setItems([]);
      setPreferences([]);
      setUnreadCount(0);
      setReady(false);
      return;
    }
    fetchList(filters.unread ? { unread: 1, category: filters.category } : { category: filters.category });
    loadPreferences();
    // intentionally run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    items,
    unreadCount,
    ready,
    loading,
    loadingMore,
    hasMore,
    error,
    filters,
    setFilters,
    fetchList,
    fetchMore,
    refreshUnread,
    applyNewNotification,
    markRead,
    markAll,
    preferences,
    loadPreferences,
    upsertPreference,
    drawerOpen,
    openDrawer,
    closeDrawer,
  }), [items, unreadCount, ready, loading, loadingMore, hasMore, error, filters, fetchList, fetchMore, refreshUnread, applyNewNotification, markRead, markAll, preferences, loadPreferences, upsertPreference, drawerOpen, openDrawer, closeDrawer]);

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
